// Client state machine driving the Auto-Formulate user journey against
// the real Phase 5a server endpoints.
//
// States:
//   "idle"       — nothing has run.
//   "starting"   — POST /auto-formulate in flight, before SSE opened.
//   "running"    — SSE open; worker computing; profile heatmaps render off
//                  the `initial` event.
//   "applying"   — `result` event received. We stage the proposed changes
//                  one at a time (adds + edits shuffled together, then a
//                  "pruning" pass for removes), with 500-1000ms random
//                  jitter between steps. Each step mutates the formulation
//                  and bumps an interpolated viz override so the Flavor
//                  Space + Sensory Radar move toward the proposal in real
//                  time.
//   "review"     — All proposed changes applied; user reviews the final
//                  proposed flask, can Undo per row or Accept / Reject.
//   "failed"     — POST or SSE failure; user dismisses via Reject.
//
// Autosave is paused on run() and resumed on accept()/reject(). Accept
// fires one save with the (possibly user-edited) Review state. Reject
// restores the pre-AF snapshot in memory; the post-resume baseline check
// recognizes "state == last saved" and skips the save.

import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { FormulationEntry } from "@/utils/types/compound";
import type { AmountUnit } from "@/services/formula_api";
import { fetchSubstanceSupplyFlagsOne } from "@/services/substance_api";
import { substanceSupplyFlagsKeys } from "@/hooks/useSubstanceSupplyFlags";
import type {
  AutoFormulateMode,
  AutoFormulateRunOpts,
} from "@/components/Workbench/Flask/AutoFormulate/AutoFormulateButton";
import type { ChangeKind } from "@/components/Workbench/Flask/AutoFormulate/ChangeSymbol";
import type { StatusEntry } from "@/components/Workbench/Flask/AutoFormulate/StatusFeed";
import {
  AutoFormulateApiError,
  autoFormulateEventsUrl,
  formulationKeyFromSse,
  startAutoFormulate,
  type AfFailedPayload,
  type AfInitialPayload,
  type AfProposedItem,
  type AfResultPayload,
  type AfResultViz,
  type AfStep,
} from "@/services/autoFormulate_api";
import type { VizPayload } from "@/services/workbench_api";
import monitoring from "@/lib/monitoring";
import { registerAutoFormulateIngredients, registerServerLens } from "@/utils/serverCompounds";

// DEMO (impl-demo-mock) — honor a proposed AF unit when it's a real AmountUnit
// (e.g. the demo recipe's 'ppm'). Real nblock emits "ratio"/"mol/kg"/"" which
// aren't enum values, so those still fall back to mol%. Remove with services/demo.
const AF_AMOUNT_UNITS = new Set<AmountUnit>([
  "ppm", "ppb", "ppt", "mol%", "mg", "g", "kg", "μL", "mL", "L",
]);
function afUnitOrMolPercent(u: string | null | undefined): AmountUnit {
  return u != null && AF_AMOUNT_UNITS.has(u as AmountUnit) ? (u as AmountUnit) : "mol%";
}

export type AIChange = {
  kind: ChangeKind;
  compoundId: string;
  prevPpm?: number;
  newPpm?: number;
  // For "add" undo and "remove" restore — preserve the unit if we know it.
  prevUnit?: AmountUnit;
  newUnit?: AmountUnit;
};

export type AIPhase =
  | "idle"
  | "starting"
  | "running"
  | "applying"
  | "review"
  // Accept clicked; the proposed formulation is being persisted via the
  // autosave flush + viz computation. The proposed viz override stays on
  // (orange) during this window so the user doesn't see their pre-AF
  // formulation flash through between Accept and the new viz landing.
  // finalize() drops the override the moment onVersionSaved patches the
  // active variant's currentVersionId with the new (already-cached) viz.
  | "saving"
  | "failed";

export type AutoFormulateState = {
  phase: AIPhase;
  mode: AutoFormulateMode | null;
  jobId: string | null;

  /** Original formulation captured at run() — used by Reject + Undo. */
  snapshot: FormulationEntry[] | null;

  /** Per-ingredient profile vectors from the `initial` event. */
  ingredientProfiles: Map<string, number[] | null>;
  /** Compound profile vectors from food composition (target food). */
  foodCompositionProfiles: Map<number, number[] | null>;
  /** Profiles for items added by AI (merged into ingredientProfiles on result). */
  newIngredientProfiles: Map<string, number[] | null>;

  /** Result-event proposed items, ordered by position_in_set. */
  proposedItems: AfProposedItem[] | null;
  /** Bundled dry-run viz from the `result` event. */
  proposedViz: AfResultViz | null;

  /** Live in-flight viz override during the `applying` phase. Interpolated
   *  between the AF-start snapshot viz and proposedViz, 1/N per applied
   *  change so the Flavor Space + Sensory Radar move smoothly toward the
   *  proposal as each ingredient is staged. Null outside `applying`. */
  inflightViz: AfResultViz | null;
  /** Latest Review-stage recompute previewId (server cache key) — threaded
   *  into the Add-Ingredients endpoints so they reflect the edited proposal.
   *  Null outside Review / before the first recompute resolves. */
  reviewPreviewId: string | null;
  /** Latest Review-stage recompute VizPayload — fed into aiVizOverride so the
   *  Radar + Flavor Space react to edits during Review. */
  reviewViz: VizPayload | null;

  statusLog: StatusEntry[];
  changes: AIChange[];

  /** Set when phase === 'failed'. */
  errorMessage: string | null;

  run: (opts: AutoFormulateRunOpts) => void;
  accept: () => void;
  reject: () => void;
  undoOne: (compoundId: string) => void;
  /**
   * Drop AF state after the Accept-triggered save lands. WorkbenchPage
   * wires this into the autosave's onVersionSaved callback so the proposed
   * viz override is removed in the same render commit as the active
   * variant's currentVersionId gets patched (and useWorkbenchViz reads
   * the new viz from the React Query cache that saveVersion pre-populated).
   * No-op outside the 'saving' phase.
   */
  finalize: () => void;
  /** Push the result of a Review-stage recompute (called by WorkbenchPage's
   *  onReviewRecompute). No-op outside the 'review' phase. */
  setReviewResult: (previewId: string, viz: VizPayload) => void;
};

export type UseAutoFormulateArgs = {
  flavorId: string | null;
  formulaId: number | null;
  versionId: number | null;
  /** Current flask formulation — read for snapshot at run() time. */
  formulation: FormulationEntry[];
  /** Bulk setter that replaces the active variant's formulation. */
  setFormulation: (next: FormulationEntry[]) => void;
  /** Pause/resume autosave around the AF lifecycle. */
  pauseAutosave: () => void;
  resumeAutosave: () => void;
  /**
   * Fire a save immediately (bypassing the autosave debounce). accept()
   * calls this so the proposed formulation is persisted + its viz is
   * computed BEFORE the AF overlay drops — orange flips straight to
   * green with no intermediate flash through the pre-AF viz.
   */
  flushAutosave: () => Promise<void>;
  /** Display-name resolver — purely for narration strings. */
  resolveName?: (compoundId: string) => string;
  /** Latest workbench viz read by WorkbenchPage. Snapshotted at run()-time
   *  as the start-of-formulation viz; the apply phase interpolates from
   *  here toward proposedViz one step per change. Hook reads it via ref,
   *  so passing a fresh object on every render is fine. */
  liveViz?: AfResultViz | null;
};

const STILL_COMPUTING_MS = 30_000;

export function useAutoFormulate({
  flavorId,
  formulaId,
  versionId,
  formulation,
  setFormulation,
  pauseAutosave,
  resumeAutosave,
  flushAutosave,
  resolveName,
  liveViz,
}: UseAutoFormulateArgs): AutoFormulateState {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<AIPhase>("idle");
  const [mode, setMode] = useState<AutoFormulateMode | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<FormulationEntry[] | null>(null);
  const [ingredientProfiles, setIngredientProfiles] = useState<
    Map<string, number[] | null>
  >(new Map());
  const [foodCompositionProfiles, setFoodCompositionProfiles] = useState<
    Map<number, number[] | null>
  >(new Map());
  const [newIngredientProfiles, setNewIngredientProfiles] = useState<
    Map<string, number[] | null>
  >(new Map());
  const [proposedItems, setProposedItems] = useState<AfProposedItem[] | null>(
    null,
  );
  const [proposedViz, setProposedViz] = useState<AfResultViz | null>(null);
  const [inflightViz, setInflightViz] = useState<AfResultViz | null>(null);
  const [reviewPreviewId, setReviewPreviewId] = useState<string | null>(null);
  const [reviewViz, setReviewViz] = useState<VizPayload | null>(null);
  const [statusLog, setStatusLog] = useState<StatusEntry[]>([]);
  const [changes, setChanges] = useState<AIChange[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Snapshot of the workbench viz captured at run() time. Used as the
  // start point for the apply-phase interpolation. Stored in a ref (not
  // state) — never read during render, so no re-render needed when set.
  const snapshotVizRef = useRef<AfResultViz | null>(null);
  // Latest live viz from the parent, read at run() to capture the
  // snapshot. We don't subscribe to changes — only the value at run()
  // time matters.
  const liveVizRef = useRef<AfResultViz | null>(liveViz ?? null);
  liveVizRef.current = liveViz ?? null;
  // Abort handle for the apply-phase setTimeout chain. Lets reject() /
  // unmount / version-change bail out without finishing the staged
  // updates.
  const applyAbortRef = useRef<{ aborted: boolean } | null>(null);

  // Latest formulation in a ref so SSE callbacks read current state without
  // re-creating the EventSource subscriptions.
  const formulationRef = useRef<FormulationEntry[]>(formulation);
  formulationRef.current = formulation;
  const setFormulationRef = useRef(setFormulation);
  setFormulationRef.current = setFormulation;

  // SSE + POST abort handles for cleanup.
  const esRef = useRef<EventSource | null>(null);
  const postAbortRef = useRef<AbortController | null>(null);
  const stillComputingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // Track current phase in a ref so SSE callbacks can read it.
  const phaseRef = useRef<AIPhase>("idle");
  phaseRef.current = phase;

  // Cleanup helpers.
  const closeSse = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    if (stillComputingTimerRef.current) {
      clearTimeout(stillComputingTimerRef.current);
      stillComputingTimerRef.current = null;
    }
  }, []);

  const cleanupAfter = useCallback(() => {
    closeSse();
    postAbortRef.current?.abort();
    postAbortRef.current = null;
  }, [closeSse]);

  const resetState = useCallback(() => {
    setPhase("idle");
    setMode(null);
    setJobId(null);
    setSnapshot(null);
    setIngredientProfiles(new Map());
    setFoodCompositionProfiles(new Map());
    setNewIngredientProfiles(new Map());
    setProposedItems(null);
    setProposedViz(null);
    setInflightViz(null);
    setReviewPreviewId(null);
    setReviewViz(null);
    setStatusLog([]);
    setChanges([]);
    setErrorMessage(null);
    snapshotVizRef.current = null;
    if (applyAbortRef.current) {
      applyAbortRef.current.aborted = true;
      applyAbortRef.current = null;
    }
  }, []);

  const nameOf = useCallback(
    (id: string) => {
      if (resolveName) return resolveName(id);
      const idx = id.indexOf(":");
      return idx >= 0 ? id.slice(idx + 1) : id;
    },
    [resolveName],
  );

  // Build the AIChange[] diff between snapshot and proposed items.
  const buildChanges = useCallback(
    (
      snap: FormulationEntry[],
      proposed: AfProposedItem[],
    ): AIChange[] => {
      const snapByKey = new Map<string, FormulationEntry>();
      for (const f of snap) snapByKey.set(f.compoundId, f);

      const proposedByKey = new Map<
        string,
        { ppm: number; unit: AmountUnit }
      >();
      for (const p of proposed) {
        const key = formulationKeyFromSse(p.ndbTable, p.rowid);
        proposedByKey.set(key, {
          ppm: p.concentration ?? 0,
          // nblock's compute is intrinsically in molar ratios — it stores
          // "ratio" / "mol/kg" / "" in result_item.unit, not a unit name
          // that maps cleanly onto our enum, so those fall back to mol%.
          // A clean enum unit (e.g. the demo recipe's 'ppm') is honored.
          unit: afUnitOrMolPercent(p.unit),
        });
      }

      const out: AIChange[] = [];
      // Adds + edits.
      for (const [key, prop] of proposedByKey) {
        const prior = snapByKey.get(key);
        if (!prior) {
          out.push({
            kind: "add",
            compoundId: key,
            newPpm: prop.ppm,
            newUnit: prop.unit,
          });
        } else if (
          Math.abs(prior.ppm - prop.ppm) > 1e-9 ||
          (prior.amountUnit ?? "ppm") !== prop.unit
        ) {
          out.push({
            kind: "edit",
            compoundId: key,
            prevPpm: prior.ppm,
            newPpm: prop.ppm,
            prevUnit: prior.amountUnit as AmountUnit | undefined,
            newUnit: prop.unit,
          });
        }
      }
      // Removes.
      for (const [key, prior] of snapByKey) {
        if (!proposedByKey.has(key)) {
          out.push({
            kind: "remove",
            compoundId: key,
            prevPpm: prior.ppm,
            prevUnit: prior.amountUnit as AmountUnit | undefined,
          });
        }
      }
      return out;
    },
    [],
  );

  // ── Apply-phase staging ──────────────────────────────────────────────
  //
  // After the worker returns its proposal, instead of snapping the flask
  // to the final state we stage one change at a time so the user can see
  // N-Block "thinking." Order is intentional:
  //
  //   1. Adds + edits shuffled together (build-up phase).
  //   2. A single "Pruning…" status, then removes shuffled (cleanup).
  //
  // Between steps we wait 500-1000ms (uniform random) and bump the
  // interpolated viz override so Flavor Space + Sensory Radar drift
  // toward the proposal in real time. The override is published via
  // setInflightViz; WorkbenchPage feeds it into aiVizOverride alongside
  // the existing review-phase path.

  /** Narration templates. One pool per step type, picked uniformly. */
  const ADD_TEMPLATES = [
    "Introducing {name}",
    "Mixing in {name}",
    "Adding {name} to the blend",
    "Blending in {name}",
    "Incorporating {name}",
  ] as const;
  const EDIT_TEMPLATES = [
    "Tuning {name}",
    "Adjusting {name}",
    "Re-balancing {name}",
    "Refining {name}'s concentration",
  ] as const;
  const PRUNE_INTRO_TEMPLATES = [
    "Pruning redundant ingredients…",
    "Trimming the blend — removing ingredients that don't contribute…",
    "Cleaning up: discarding ingredients that aren't pulling their weight…",
  ] as const;
  const REMOVE_TEMPLATES = [
    "Pruning {name}",
    "Removing {name} — redundant for this target",
    "Trimming {name}",
    "Dropping {name}",
  ] as const;
  const FINAL_TEMPLATE = "Proposal ready — review changes below.";

  const pickTemplate = useCallback(
    (pool: readonly string[], compoundName: string | null) => {
      const tpl = pool[Math.floor(Math.random() * pool.length)] ?? pool[0]!;
      return compoundName ? tpl.replace("{name}", compoundName) : tpl;
    },
    [],
  );

  /** Shuffle a copy of the input (Fisher–Yates). Pure; does not mutate. */
  const shuffle = useCallback(<T>(arr: T[]): T[] => {
    const out = arr.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [out[i], out[j]] = [out[j]!, out[i]!];
    }
    return out;
  }, []);

  /** 500-1000ms (inclusive) uniform random delay. */
  const stepDelay = useCallback(
    () => 500 + Math.floor(Math.random() * 501),
    [],
  );

  /** Linear-interpolate two scalars (a → b, t ∈ [0,1]). NaN-safe. */
  const lerp = useCallback((a: number | null, b: number | null, t: number) => {
    if (a == null && b == null) return null;
    if (a == null) return b;
    if (b == null) return a;
    return a + (b - a) * t;
  }, []);

  /** Interpolate two equal-length number arrays. Returns null if either
   *  side is missing. */
  const lerpVec = useCallback(
    (a: number[] | null, b: number[] | null, t: number): number[] | null => {
      if (!a && !b) return null;
      if (!a) return b ?? null;
      if (!b) return a ?? null;
      const n = Math.min(a.length, b.length);
      const out = new Array<number>(n);
      for (let i = 0; i < n; i++) out[i] = a[i]! + (b[i]! - a[i]!) * t;
      return out;
    },
    [],
  );

  /** Interpolate the keyed-dict radar fields. Falls back to whichever
   *  side is present per key. */
  const lerpDict = useCallback(
    (
      a: Record<string, number> | null,
      b: Record<string, number> | null,
      t: number,
    ): Record<string, number> | null => {
      if (!a && !b) return null;
      const keys = new Set<string>([
        ...(a ? Object.keys(a) : []),
        ...(b ? Object.keys(b) : []),
      ]);
      const out: Record<string, number> = {};
      for (const k of keys) {
        const av = a?.[k];
        const bv = b?.[k];
        if (av == null && bv == null) continue;
        if (av == null) out[k] = bv!;
        else if (bv == null) out[k] = av;
        else out[k] = av + (bv - av) * t;
      }
      return out;
    },
    [],
  );

  const interpolateViz = useCallback(
    (from: AfResultViz | null, to: AfResultViz, t: number): AfResultViz => ({
      profile: lerpVec(from?.profile ?? null, to.profile, t),
      drCoord:
        !from?.drCoord || !to.drCoord
          ? to.drCoord
          : {
              x: from.drCoord.x + (to.drCoord.x - from.drCoord.x) * t,
              y: from.drCoord.y + (to.drCoord.y - from.drCoord.y) * t,
              z:
                from.drCoord.z != null && to.drCoord.z != null
                  ? from.drCoord.z + (to.drCoord.z - from.drCoord.z) * t
                  : (to.drCoord.z ?? from.drCoord.z ?? null),
            },
      loss: lerp(from?.loss ?? null, to.loss, t),
      tasteRadar: lerpDict(from?.tasteRadar ?? null, to.tasteRadar, t),
      smellRadar: lerpDict(from?.smellRadar ?? null, to.smellRadar, t),
    }),
    [lerp, lerpDict, lerpVec],
  );

  /** Apply a single AIChange to `formulation` via setFormulationRef. */
  const applyOneChange = useCallback((ch: AIChange) => {
    const cur = formulationRef.current;
    if (ch.kind === "add") {
      if (cur.some((x) => x.compoundId === ch.compoundId)) return;
      setFormulationRef.current([
        ...cur,
        {
          compoundId: ch.compoundId,
          ppm: ch.newPpm ?? 1,
          // AF-driven adds default to mol% — see buildChanges above.
          amountUnit: ch.newUnit ?? "mol%",
        },
      ]);
    } else if (ch.kind === "edit") {
      setFormulationRef.current(
        cur.map((x) =>
          x.compoundId === ch.compoundId
            ? {
                ...x,
                ppm: ch.newPpm ?? x.ppm,
                ...(ch.newUnit ? { amountUnit: ch.newUnit } : {}),
              }
            : x,
        ),
      );
    } else if (ch.kind === "remove") {
      setFormulationRef.current(
        cur.filter((x) => x.compoundId !== ch.compoundId),
      );
    }
  }, []);

  /** Schedule the staged apply pass. Returns immediately; the chain runs
   *  via nested setTimeouts so we can cancel mid-stream by setting
   *  applyAbortRef.current.aborted = true. Final state: phase='review'
   *  once the last step lands.
   *
   *  When `serverSteps` is supplied (current servers), we play the server's
   *  ordered sequence and use its real per-step viz for the Flavor Space
   *  + Radar plots — Current's drCoord and the radar values now reflect
   *  the ACTUAL mS at each AI alteration rather than a linear interpolation
   *  between start and final state.
   *
   *  When `serverSteps` is undefined (older server build), we fall back to
   *  the prior path: shuffle adds+edits and removes, drive the chart via
   *  linear interpolation between snapshotVizStart and proposedVizFinal. */
  const runApplyPhase = useCallback(
    (
      diff: AIChange[],
      proposedVizFinal: AfResultViz,
      snapshotVizStart: AfResultViz | null,
      serverSteps: AfStep[] | undefined,
    ) => {
      const abort = { aborted: false };
      applyAbortRef.current = abort;

      // ── New path: server-defined sequence with real per-step viz ──────
      if (serverSteps && serverSteps.length > 0) {
        // Convert each server step to the client's AIChange shape so we
        // can reuse applyOneChange + the narration templates without
        // duplicating mutation logic. Note: client forces newUnit='mol%'
        // (matches buildChanges in this file) because nblock emits raw
        // ratio/mol-kg strings the rest of the UI doesn't understand.
        const aiChangeFromServerStep = (s: AfStep): AIChange => {
          const compoundId = formulationKeyFromSse(
            s.mutation.ndbTable,
            s.mutation.rowid,
          );
          return {
            kind: s.mutation.kind,
            compoundId,
            ...(s.mutation.prevPpm != null && { prevPpm: s.mutation.prevPpm }),
            ...(s.mutation.newPpm != null && { newPpm: s.mutation.newPpm }),
            ...(s.mutation.prevUnit != null && {
              prevUnit: s.mutation.prevUnit as AmountUnit,
            }),
            // DEMO (impl-demo-mock) — honor the step's unit when it's a real
            // AmountUnit (the demo recipe's 'ppm'); real nblock units fall
            // back to mol%. Remove with services/demo.
            ...(s.mutation.kind !== "remove" && {
              newUnit: afUnitOrMolPercent(s.mutation.newUnit),
            }),
          };
        };

        const runStep = (idx: number) => {
          if (abort.aborted) return;
          if (idx >= serverSteps.length) {
            // Final landing — guarantee viz matches proposal exactly so
            // the Review panel and the just-played last step agree.
            setInflightViz(proposedVizFinal);
            setStatusLog((log) => [
              ...log,
              { id: "final", kind: "info", text: FINAL_TEMPLATE },
            ]);
            setPhase("review");
            applyAbortRef.current = null;
            return;
          }
          const step = serverSteps[idx]!;
          const ch = aiChangeFromServerStep(step);
          const name = nameOf(ch.compoundId);
          const pool =
            ch.kind === "add"
              ? ADD_TEMPLATES
              : ch.kind === "edit"
                ? EDIT_TEMPLATES
                : REMOVE_TEMPLATES;
          applyOneChange(ch);
          // Real per-step viz from the server — no interpolation.
          setInflightViz(step.viz);
          setStatusLog((log) => [
            ...log,
            {
              id: `step-${idx}-${ch.compoundId}`,
              kind: "info",
              text: pickTemplate(pool, name),
            },
          ]);
          const wait = stepDelay();
          setTimeout(() => runStep(idx + 1), wait);
        };

        // 0-change case: empty steps shouldn't happen if we gated on
        // length > 0 above, but be defensive.
        if (serverSteps.length === 0) {
          setInflightViz(proposedVizFinal);
          setStatusLog((log) => [
            ...log,
            { id: "no-changes", kind: "info", text: "No changes proposed." },
            { id: "final", kind: "info", text: FINAL_TEMPLATE },
          ]);
          setPhase("review");
          return;
        }
        setTimeout(() => runStep(0), 300);
        return;
      }

      // ── Fallback: older server build (no steps) — keep prior linear-
      //    interpolation behavior so deploys can roll forward/back without
      //    coordinating across client/server. ──────────────────────────────
      const addsAndEdits = shuffle(
        diff.filter((c) => c.kind === "add" || c.kind === "edit"),
      );
      const removes = shuffle(diff.filter((c) => c.kind === "remove"));

      if (addsAndEdits.length === 0 && removes.length === 0) {
        setInflightViz(proposedVizFinal);
        setStatusLog((log) => [
          ...log,
          { id: "no-changes", kind: "info", text: "No changes proposed." },
          { id: "final", kind: "info", text: FINAL_TEMPLATE },
        ]);
        setPhase("review");
        return;
      }

      type Step =
        | { kind: "change"; change: AIChange }
        | { kind: "prune-intro" };
      const stepsLegacy: Step[] = [];
      for (const ch of addsAndEdits) stepsLegacy.push({ kind: "change", change: ch });
      if (removes.length > 0) {
        stepsLegacy.push({ kind: "prune-intro" });
        for (const ch of removes) stepsLegacy.push({ kind: "change", change: ch });
      }

      const totalMutations = addsAndEdits.length + removes.length;
      let mutationIdx = 0;

      const runLegacyStep = (idx: number) => {
        if (abort.aborted) return;
        if (idx >= stepsLegacy.length) {
          setInflightViz(proposedVizFinal);
          setStatusLog((log) => [
            ...log,
            { id: "final", kind: "info", text: FINAL_TEMPLATE },
          ]);
          setPhase("review");
          applyAbortRef.current = null;
          return;
        }

        const step = stepsLegacy[idx]!;
        if (step.kind === "prune-intro") {
          setStatusLog((log) => [
            ...log,
            {
              id: `prune-intro-${idx}`,
              kind: "info",
              text: pickTemplate(PRUNE_INTRO_TEMPLATES, null),
            },
          ]);
        } else {
          const ch = step.change;
          const name = nameOf(ch.compoundId);
          const pool =
            ch.kind === "add"
              ? ADD_TEMPLATES
              : ch.kind === "edit"
                ? EDIT_TEMPLATES
                : REMOVE_TEMPLATES;
          applyOneChange(ch);
          mutationIdx += 1;
          const t = mutationIdx / totalMutations;
          setInflightViz(interpolateViz(snapshotVizStart, proposedVizFinal, t));
          setStatusLog((log) => [
            ...log,
            {
              id: `step-${idx}-${ch.compoundId}`,
              kind: "info",
              text: pickTemplate(pool, name),
            },
          ]);
        }

        const wait = stepDelay();
        setTimeout(() => runLegacyStep(idx + 1), wait);
      };

      setTimeout(() => runLegacyStep(0), 300);
    },
    [
      applyOneChange,
      interpolateViz,
      pickTemplate,
      shuffle,
      stepDelay,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
  );

  // ── SSE event handlers ────────────────────────────────────────────────

  const handleInitial = useCallback((payload: AfInitialPayload) => {
    // Seed the runtime compound registry from the SSE payload. New
    // compounds that haven't been touched by other endpoints (e.g.
    // formula items) need this to resolve through compoundById — without
    // the name, FlaskPanel's row grouping at line 298-300 skips the row
    // entirely (compound not found → continue), so AI-added ingredients
    // would render as missing rows.
    registerAutoFormulateIngredients(
      payload.ingredients.map((it) => ({
        ndbTable: it.ndbTable,
        rowid: it.rowid,
        name: it.name ?? null,
      })),
    );

    const ing = new Map<string, number[] | null>();
    for (const it of payload.ingredients) {
      ing.set(formulationKeyFromSse(it.ndbTable, it.rowid), it.profile);
    }
    setIngredientProfiles(ing);
    const fc = new Map<number, number[] | null>();
    for (const it of payload.foodComposition) {
      fc.set(it.compoundId, it.profile);
    }
    setFoodCompositionProfiles(fc);
    setStatusLog((log) => [
      ...log,
      {
        id: "initial",
        kind: "info",
        text: "AI evaluating formulation candidates…",
      },
    ]);
    // Arm the "still computing" hint after 30s.
    if (stillComputingTimerRef.current) {
      clearTimeout(stillComputingTimerRef.current);
    }
    stillComputingTimerRef.current = setTimeout(() => {
      setStatusLog((log) => [
        ...log,
        {
          id: "still",
          kind: "info",
          text: "Still computing — complex formulations take a moment.",
        },
      ]);
    }, STILL_COMPUTING_MS);
  }, []);

  const handleResult = useCallback(
    (payload: AfResultPayload) => {
      if (stillComputingTimerRef.current) {
        clearTimeout(stillComputingTimerRef.current);
        stillComputingTimerRef.current = null;
      }
      // Register the new ingredients in the runtime registry so their
      // names resolve before applyOneChange tries to render them — the
      // FlaskPanel groups rows via compoundById and SKIPS rows whose
      // compoundId isn't resolvable (line 298-300), so without this
      // step new ingredients added during the apply phase are invisible.
      registerAutoFormulateIngredients(
        payload.newIngredientProfiles.map((it) => ({
          ndbTable: it.ndbTable,
          rowid: it.rowid,
          name: it.name ?? null,
          cas: it.cas ?? null,
        })),
      );
      // Register full lens (family / chemClass / role / flavor labels) for the
      // proposed ingredients so FlaskRow keeps its family color + chem tag +
      // CAS the instant the proposal is accepted — no strip/flash waiting on
      // the later formulas-list refetch. (The result event carries lens+cas;
      // the initial event does not, hence the null filter.)
      const afLensRows = payload.newIngredientProfiles
        .filter((it) => it.lens != null)
        .map((it) => ({
          compoundId: `${it.ndbTable === "public.food" ? "food" : "compound"}:${it.rowid}`,
          name: it.name ?? undefined,
          cas: it.cas ?? null,
          kind: (it.ndbTable === "public.food" ? "food" : "compound") as
            | "food"
            | "compound",
          lens: it.lens!,
        }));
      if (afLensRows.length > 0) registerServerLens(afLensRows);

      // Prefetch supply flags for every new compound in ONE batched
      // network call (the per-key prefetchQuery calls land in the same
      // tick → fetchSubstanceSupplyFlagsOne's coalescer merges them
      // into a single POST). Without this each apply step's add would
      // fire its own batch with just one key, since steps run 500-
      // 1000ms apart — the coalescer can only merge within a tick.
      if (flavorId !== null) {
        for (const it of payload.newIngredientProfiles) {
          const key = formulationKeyFromSse(it.ndbTable, it.rowid);
          void queryClient.prefetchQuery({
            queryKey: substanceSupplyFlagsKeys.one(flavorId, key),
            queryFn: () => fetchSubstanceSupplyFlagsOne(key),
            staleTime: 30_000,
          });
        }
      }

      // Merge new-ingredient profiles into the ingredient profile map so
      // the heatmap rows for incoming compounds have data ready when the
      // apply phase reveals them one by one.
      const newProfiles = new Map<string, number[] | null>();
      for (const it of payload.newIngredientProfiles) {
        newProfiles.set(
          formulationKeyFromSse(it.ndbTable, it.rowid),
          it.profile,
        );
      }
      setNewIngredientProfiles(newProfiles);
      setIngredientProfiles((prev) => {
        const merged = new Map(prev);
        for (const [k, v] of newProfiles) merged.set(k, v);
        return merged;
      });
      setProposedItems(payload.proposedItems);
      setProposedViz(payload.viz);

      // Diff against snapshot to populate change pills + per-row Undo.
      // (Same diff drives the apply-phase staging.)
      const snap = formulationRef.current;
      const diff = buildChanges(snap, payload.proposedItems);
      setChanges(diff);

      // Hand off to the staged apply phase. NOT applyProposed() any more —
      // we want the flask to mutate one change at a time so the user can
      // watch N-Block "think". runApplyPhase eventually flips phase to
      // 'review' when the chain finishes.
      setStatusLog((log) => [
        ...log,
        {
          id: "result",
          kind: "info",
          text: "Proposal received. Applying changes…",
        },
      ]);
      setPhase("applying");
      setInflightViz(snapshotVizRef.current);
      runApplyPhase(diff, payload.viz, snapshotVizRef.current, payload.steps);
    },
    [buildChanges, runApplyPhase, queryClient, flavorId],
  );

  const handleFailed = useCallback(
    (error: string) => {
      if (stillComputingTimerRef.current) {
        clearTimeout(stillComputingTimerRef.current);
        stillComputingTimerRef.current = null;
      }
      setErrorMessage(error);
      setStatusLog((log) => [
        ...log,
        {
          id: "failed",
          kind: "info",
          text: `Auto-Formulate failed: ${error}`,
        },
      ]);
      setPhase("failed");
      closeSse();
    },
    [closeSse],
  );

  // ── run() ─────────────────────────────────────────────────────────────

  const run = useCallback(
    (opts: AutoFormulateRunOpts) => {
      if (flavorId === null || formulaId === null || versionId === null) {
        return;
      }
      // Capture snapshot + flip into starting state. Pause autosave NOW so
      // any pending debounce from the prior edit can't sneak a save in.
      pauseAutosave();
      const snap = formulationRef.current.map((f) => ({ ...f }));
      setSnapshot(snap);
      // Capture the current workbench viz as the apply-phase start point.
      // Stored shallowly — proposedViz arrives later via the SSE result
      // event, and runApplyPhase reads both as the interp endpoints.
      snapshotVizRef.current = liveVizRef.current
        ? { ...liveVizRef.current }
        : null;
      setMode(opts.mode);
      setStatusLog([
        {
          id: "starting",
          kind: "info",
          text: "Submitting formulation to AI…",
        },
      ]);
      setErrorMessage(null);
      setIngredientProfiles(new Map());
      setFoodCompositionProfiles(new Map());
      setNewIngredientProfiles(new Map());
      setProposedItems(null);
      setProposedViz(null);
      setInflightViz(null);
      setReviewPreviewId(null);
      setReviewViz(null);
      setChanges([]);
      setPhase("starting");

      const ac = new AbortController();
      postAbortRef.current = ac;

      void startAutoFormulate(
        flavorId,
        formulaId,
        versionId,
        { mode: opts.mode, ...(opts.prompt ? { prompt: opts.prompt } : {}) },
        ac.signal,
      )
        .then(({ jobId: newJobId }) => {
          setJobId(newJobId);
          setPhase("running");
          const es = new EventSource(
            autoFormulateEventsUrl(flavorId, formulaId, versionId),
            { withCredentials: true },
          );
          esRef.current = es;
          es.addEventListener("initial", (e) => {
            try {
              const payload = JSON.parse(
                (e as MessageEvent).data,
              ) as AfInitialPayload;
              handleInitial(payload);
            } catch {
              handleFailed("malformed_initial_event");
            }
          });
          es.addEventListener("result", (e) => {
            try {
              const payload = JSON.parse(
                (e as MessageEvent).data,
              ) as AfResultPayload;
              handleResult(payload);
            } catch {
              handleFailed("malformed_result_event");
            }
            closeSse();
          });
          es.addEventListener("failed", (e) => {
            let err = "unknown";
            try {
              const payload = JSON.parse(
                (e as MessageEvent).data,
              ) as AfFailedPayload;
              err = payload.error || "unknown";
            } catch {
              /* ignore */
            }
            handleFailed(err);
          });
          es.onerror = () => {
            // EventSource fires onerror on benign close too. Distinguish
            // via phase: the result-event handler closes the ES on receipt
            // and we transition to `applying` → onerror then fires
            // immediately. Same for the post-review teardown. Both are
            // expected; only treat onerror as a real connection_lost when
            // we're still in the pre-result phases.
            const p = phaseRef.current;
            if (
              p === "applying" ||
              p === "review" ||
              p === "idle" ||
              p === "failed"
            ) {
              return;
            }
            handleFailed("connection_lost");
          };
        })
        .catch((err: unknown) => {
          if ((err as { name?: string })?.name === "AbortError") return;
          const code =
            err instanceof AutoFormulateApiError && err.code
              ? err.code
              : "start_failed";
          handleFailed(code);
        });
    },
    [
      flavorId,
      formulaId,
      versionId,
      pauseAutosave,
      handleInitial,
      handleResult,
      handleFailed,
      closeSse,
    ],
  );

  // ── accept() ──────────────────────────────────────────────────────────

  // Safety-net timer for the saving phase: if the flush save fails (the
  // autosave hook catches + logs but doesn't surface the failure) or is
  // deduped without firing onVersionSaved, we'd otherwise be stuck on the
  // orange overlay forever. Fall back to a clean state after a bounded
  // wait. saveVersion's inline computeViz takes ~5–10s on cold cache, so
  // 30s is generous; finalize() is the happy path and will fire first.
  const SAVING_FALLBACK_MS = 30_000;

  const accept = useCallback(() => {
    cleanupAfter();
    // Hold the proposal overlay in place — don't resetState yet. The save
    // round-trip below carries the inline-computed viz back; WorkbenchPage's
    // onVersionSaved primes the React Query cache and calls finalize(),
    // which then drops the override in the SAME render commit that
    // currentVersionId is patched. Net effect: orange → green with no
    // intermediate flash through the pre-AF viz.
    setPhase("saving");

    void flushAutosave().catch((err: unknown) => {
      monitoring.warn("auto_formulate_accept_flush_failed", err instanceof Error ? err : new Error(String(err)));
    });

    // Belt-and-braces — finalize from WorkbenchPage.onVersionSaved is the
    // primary path; this guards against save failure / dedup / network
    // edge cases that would otherwise strand the user on the overlay.
    setTimeout(() => {
      if (phaseRef.current === "saving") {
        resumeAutosave();
        resetState();
      }
    }, SAVING_FALLBACK_MS);
  }, [cleanupAfter, flushAutosave, resumeAutosave, resetState]);

  // ── finalize() ────────────────────────────────────────────────────────

  // Called by WorkbenchPage from autosave.onVersionSaved once the
  // Accept-triggered save lands. Drops the proposal overlay so liveViz
  // (already pointing at the freshly-saved version's viz via setQueryData)
  // becomes the current source. No-op outside 'saving'.
  const finalize = useCallback(() => {
    if (phaseRef.current !== "saving") return;
    resumeAutosave();
    resetState();
  }, [resumeAutosave, resetState]);

  const setReviewResult = useCallback((previewId: string, viz: VizPayload) => {
    if (phaseRef.current !== "review") return;
    setReviewPreviewId(previewId);
    setReviewViz(viz);
  }, []);

  // ── reject() ──────────────────────────────────────────────────────────

  const reject = useCallback(() => {
    cleanupAfter();
    if (snapshot) {
      setFormulationRef.current(snapshot.map((f) => ({ ...f })));
    }
    // Resume autosave — re-arm check sees state == baseline, no save fires.
    resumeAutosave();
    resetState();
  }, [snapshot, cleanupAfter, resumeAutosave, resetState]);

  // ── undoOne() ─────────────────────────────────────────────────────────

  const undoOne = useCallback((compoundId: string) => {
    setChanges((cs) => {
      const change = cs.find((x) => x.compoundId === compoundId);
      if (!change) return cs;
      const cur = formulationRef.current;
      if (change.kind === "edit") {
        setFormulationRef.current(
          cur.map((x) =>
            x.compoundId === compoundId
              ? {
                  ...x,
                  ppm: change.prevPpm ?? x.ppm,
                  ...(change.prevUnit
                    ? { amountUnit: change.prevUnit }
                    : {}),
                }
              : x,
          ),
        );
      } else if (change.kind === "remove") {
        const exists = cur.some((x) => x.compoundId === compoundId);
        if (!exists) {
          setFormulationRef.current([
            ...cur,
            {
              compoundId,
              ppm: change.prevPpm ?? 1,
              amountUnit: change.prevUnit ?? "ppm",
            },
          ]);
        }
      } else if (change.kind === "add") {
        setFormulationRef.current(
          cur.filter((x) => x.compoundId !== compoundId),
        );
      }
      return cs.filter((x) => x.compoundId !== compoundId);
    });
  }, []);

  // ── Cleanup on unmount or input change ─────────────────────────────────

  useEffect(() => {
    return () => {
      cleanupAfter();
    };
  }, [cleanupAfter]);

  // If the active version changes mid-AF (e.g., user navigated away and
  // came back), bail to idle without restoring snapshot — the underlying
  // formulation is no longer the same flask we started with.
  useEffect(() => {
    if (phaseRef.current === "idle") return;
    cleanupAfter();
    resetState();
    // Don't resume autosave here — the version change unmounted the prior
    // WorkbenchPageInner instance entirely (see the outer-wrapper key);
    // this effect is defensive only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flavorId, formulaId]);

  // Avoid unused-variable lint when only the values are used by SSE callbacks.
  void nameOf;

  return {
    phase,
    mode,
    jobId,
    snapshot,
    ingredientProfiles,
    foodCompositionProfiles,
    newIngredientProfiles,
    proposedItems,
    proposedViz,
    inflightViz,
    reviewPreviewId,
    reviewViz,
    statusLog,
    changes,
    errorMessage,
    run,
    accept,
    reject,
    undoOne,
    finalize,
    setReviewResult,
  };
}
